import type { LiftParts, Loadout, UpgradeSystem } from './upgrades.js';
import { nextTier, UPGRADE_SYSTEMS, UPGRADES } from './upgrades.js';

/**
 * Money and what it buys.
 *
 * One soft currency, earned from finished levels, and no second one. The roadmap
 * is explicit that the economy exists to let the player experiment with upgrades
 * and nothing else at this stage — it is not a monetisation surface and must not
 * grow into one before the game is proven.
 */

/**
 * What a clear is worth, by how well it went, on an average shift. A building
 * may pay more (its `payout`, ADR-0027): a bigger building's lift costs more to
 * fit, and its shifts pay for it. Each shift pays this scaled by its place in
 * its building (`rampedPay`, ADR-0042).
 */
export const PAYOUT_BY_STARS: Readonly<Record<number, number>> = { 0: 0, 1: 18, 2: 33, 3: 51 };

/**
 * How a building's pay rises through it (ADR-0042): its first shift pays a
 * quarter of the table, its last one and three quarters, and the shifts
 * between in a straight line, so the building pays its table on average.
 *
 * A flat table paid for a part after the first shift, and a later building's
 * first shift bought four or five at once (PLAYTEST_LOG, 2026-10-22).
 */
export const PAY_RAMP = { first: 0.25, last: 1.75 } as const;

/** A shift's pay, by its place in its building: 0 for the first of `count`. */
export function rampedPay(
  table: Readonly<Record<number, number>>,
  position: number,
  count: number,
): Readonly<Record<number, number>> {
  const share =
    count <= 1 ? 1 : PAY_RAMP.first + ((PAY_RAMP.last - PAY_RAMP.first) * position) / (count - 1);
  return Object.fromEntries(
    Object.entries(table).map(([stars, coins]) => [stars, Math.round(coins * share)]),
  );
}

export interface LevelRecordLike {
  readonly stars: number;
}

/**
 * What a run pays.
 *
 * Only the improvement. Replaying a level you already three-starred earns
 * nothing, so the way to afford the next motor is to play something new or play
 * something *better* — never to grind a level you have already solved.
 *
 * That constraint comes straight from the roadmap: "do not make the player grind
 * old trivial content merely to unlock required stats." Paying for improvement
 * rather than repetition satisfies it without needing an energy meter or a daily
 * cap to hold it back.
 */
export function payoutFor(
  previousStars: number,
  newStars: number,
  table: Readonly<Record<number, number>> = PAYOUT_BY_STARS,
): number {
  const before = table[Math.max(0, previousStars)] ?? 0;
  const after = table[Math.max(0, newStars)] ?? 0;
  return Math.max(0, after - before);
}

/** What a shift's bonus goal pays (ADR-0040), as a share of its three-star pay. */
export const BONUS_SHARE = 0.2;

/**
 * What meeting a shift's bonus goal pays: once, the first time, as stars pay
 * only for improvement. A bonus met again earns nothing.
 */
export function bonusPayFor(
  firstTime: boolean,
  table: Readonly<Record<number, number>> = PAYOUT_BY_STARS,
): number {
  return firstTime ? Math.round((table[3] ?? 0) * BONUS_SHARE) : 0;
}

/**
 * The coins, and one lift's loadout. Coins are one purse across every building
 * (no second currency); a loadout belongs to the lift it was bought for
 * (ADR-0027), so a purchase is always made in one lift's parts.
 */
export interface Wallet {
  readonly coins: number;
  readonly loadout: Loadout;
}

export interface PurchaseResult {
  readonly wallet: Wallet;
  readonly system: UpgradeSystem;
  readonly tierIndex: number;
  readonly spent: number;
}

/** Install the next tier of a system, if it exists and can be afforded. */
export function purchase(
  wallet: Wallet,
  system: UpgradeSystem,
  parts: LiftParts = UPGRADES,
): PurchaseResult | null {
  const next = nextTier(system, wallet.loadout, parts);
  if (!next || next.tier.cost > wallet.coins) return null;

  return {
    wallet: {
      coins: wallet.coins - next.tier.cost,
      loadout: { ...wallet.loadout, [system]: next.index },
    },
    system,
    tierIndex: next.index,
    spent: next.tier.cost,
  };
}

/** Everything still buyable, cheapest first, with whether it is affordable now. */
export function shelf(
  wallet: Wallet,
  parts: LiftParts = UPGRADES,
): { system: UpgradeSystem; index: number; cost: number; affordable: boolean }[] {
  return UPGRADE_SYSTEMS.flatMap((system) => {
    const next = nextTier(system, wallet.loadout, parts);
    if (!next) return [];
    return [
      {
        system,
        index: next.index,
        cost: next.tier.cost,
        affordable: next.tier.cost <= wallet.coins,
      },
    ];
  }).sort((a, b) => a.cost - b.cost);
}

/**
 * What a player finishing `levelCount` levels at `stars` each would have earned.
 *
 * Two different questions run through this. At three stars it is the ceiling,
 * used to check the shop cannot be emptied by the end of the content — if it
 * can, there is no choice left to make and the roadmap's "meaningful choice
 * between two upgrade paths" has quietly stopped existing. At two stars it is
 * the realistic budget, which is the bar a level's declared par lift has to sit
 * under (ADR-0011): assuming an upgrade the average player cannot have bought
 * yet turns three stars into a purchase rather than a performance.
 */
export function earningsBy(levelCount: number, stars: 1 | 2 | 3): number {
  return levelCount * (PAYOUT_BY_STARS[stars] ?? 0);
}

/** The ceiling: three stars on everything so far. */
export function maximumEarnings(levelCount: number): number {
  return earningsBy(levelCount, 3);
}

/**
 * The parts a level assumes you have fitted, and you have not.
 *
 * Levels 21 onward are balanced for a bought lift (ADR-0011), and a player who
 * arrives without it will score badly for a reason the game never showed them.
 * "I know what I did wrong" only holds if the mismatch is visible *before* the
 * run, so this is what the level list needs to say out loud.
 *
 * Comparing tiers rather than specs on purpose: a level asks for a named part,
 * not for a number, and the answer has to be something a player can go and buy.
 */
export function partsShortOf(
  loadout: Loadout,
  par: Loadout | undefined,
  parts: LiftParts = UPGRADES,
): string[] {
  if (!par) return [];
  return UPGRADE_SYSTEMS.flatMap((system) => {
    const wanted = par[system] ?? 0;
    if ((loadout[system] ?? 0) >= wanted || wanted === 0) return [];
    return [parts[system][wanted]?.name ?? system];
  });
}
